import { Controller } from "@hotwired/stimulus"

interface SearchConfig {
  code: string
  search_field: string
  timestamp: number
}

export default class extends Controller {
  static targets = ["container", "list", "codeInput", "fieldInput"]
  static values = {
    maxItems: { type: Number, default: 5 },
    storageKey: { type: String, default: "admin_pact_recent_searches" }
  }

  declare readonly containerTarget: HTMLElement
  declare readonly listTarget: HTMLElement
  declare readonly codeInputTarget: HTMLInputElement
  declare readonly fieldInputTarget: HTMLSelectElement
  declare readonly maxItemsValue: number
  declare readonly storageKeyValue: string
  // The Stimulus `Controller` base defines `element` at runtime. Declare it for TypeScript.
  declare element: HTMLElement

  connect() {
    this.renderRecentSearches()
  }

  save(event: Event) {
    const code = this.codeInputTarget.value.trim()
    const searchField = this.fieldInputTarget.value

    if (!code) return

    const searches = this.getSearches()
    const newSearch: SearchConfig = {
      code,
      search_field: searchField,
      timestamp: Date.now()
    }

    // Remove duplicates (same code and field)
    const filteredSearches = searches.filter(
      s => !(s.code === code && s.search_field === searchField)
    )

    filteredSearches.unshift(newSearch)

    const limitedSearches = filteredSearches.slice(0, this.maxItemsValue)
    localStorage.setItem(this.storageKeyValue, JSON.stringify(limitedSearches))
  }

  renderRecentSearches() {
    const searches = this.getSearches()
    if (searches.length === 0) {
      this.containerTarget.classList.add("hidden")
      return
    }

    this.containerTarget.classList.remove("hidden")
    this.listTarget.innerHTML = ""
    
    searches.forEach(search => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs hover:bg-gray-200 transition-colors"
      button.textContent = search.code
      button.dataset.action = "click->admin--pacts--search#apply"
      button.dataset.code = search.code
      button.dataset.field = search.search_field
      this.listTarget.appendChild(button)
    })
  }

  apply(event: MouseEvent) {
    const button = event.currentTarget as HTMLButtonElement
    const code = button.dataset.code
    const field = button.dataset.field

    if (code) this.codeInputTarget.value = code
    if (field) this.fieldInputTarget.value = field

    // Submit the form
    const form = this.element instanceof HTMLFormElement ? (this.element as HTMLFormElement) : (this.element.closest("form") as HTMLFormElement | null)
    if (!form) return

    // Use requestSubmit if available, otherwise fall back to submit()
    if (typeof (form as any).requestSubmit === 'function') {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  private getSearches(): SearchConfig[] {
    const data = localStorage.getItem(this.storageKeyValue)
    if (!data) return []
    try {
      return JSON.parse(data)
    } catch {
      return []
    }
  }
}
